import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Typography, Skeleton } from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary, { accordionSummaryClasses } from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { fetchMenus } from "../../features/rbac/rbacSlice";
import { logout } from "../../features/auth/authSlice";
import { useNavigate } from "react-router-dom";
import { getSocket } from "../../services/socket";

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": { borderBottom: 0 },
  "&::before": { display: "none" },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
    transform: "rotate(90deg)",
  },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
  ...theme.applyStyles("dark", { backgroundColor: "rgba(255, 255, 255, .05)" }),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

// ─── Skeleton for one menu item ───────────────────────────────────────────────
const MenuItemSkeleton = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.25 }}>
    <Skeleton variant="circular" width={20} height={20} />
    <Skeleton variant="text" width={120} height={18} />
  </Box>
);

const LeftSidebarMenu = ({ toggleActive }) => {
  const [expanded, setExpanded] = useState(false);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { menus, loading } = useAppSelector((state) => state.rbac);

  // ── Watch <html> class for dark-theme ──────────────────────────────────────
  // const [isDark, setIsDark] = useState(
  //   () => document.querySelector("html")?.classList.contains("dark-theme") ?? false
  // );
  const [isDark, setIsDark] = useState(
  () => localStorage.getItem("theme") === "dark"
);

  useEffect(() => {
    const html = document.querySelector("html");
    if (!html) return;

    const observer = new MutationObserver(() => {
      setIsDark(html.classList.contains("dark-theme"));
    });

    observer.observe(html, { attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  // ──────────────────────────────────────────────────────────────────────────

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const isActiveLink = (path) => location.pathname === path ? "active" : "";

  // Auto-expand accordion whose page matches current route
  useEffect(() => {
    if (!menus?.length) return;
    const activeMenu = menus.find(
      (menu) => menu.pages?.some((page) => page.route === location.pathname)
    );
    if (activeMenu) setExpanded(activeMenu._id);
  }, [location.pathname, menus]);

  // Dark sidebar class only for beauty-salon page
  const [isSalonDark, setIsSalonDark] = useState(false);
  useEffect(() => {
    if (location.pathname === "/dashboard/beauty-salon/") {
      const stored = localStorage.getItem("beautySalonSidebarTheme");
      if (stored) { setIsSalonDark(stored === "dark-theme"); }
      else { setIsSalonDark(true); localStorage.setItem("beautySalonSidebarTheme", "dark-theme"); }
    } else { setIsSalonDark(false); }
  }, [location.pathname]);

  useEffect(() => { dispatch(fetchMenus()); }, [dispatch]);

  // Re-fetch whenever any menu/page is created, updated, or deleted
  useEffect(() => {
    const socket = getSocket();
    const handler = () => dispatch(fetchMenus());
    socket.on("MENU_UPDATED", handler);
    return () => socket.off("MENU_UPDATED", handler);
  }, [dispatch]);

  return (
    <Box
      className={`leftSidebarDark hide-for-horizontal-nav ${location.pathname === "/dashboard/beauty-salon/" && isSalonDark ? "dark-theme" : ""
        }`}
    >
      <Box className="left-sidebar-menu">

        {/* ── Logo — conditionally renders light/dark version ── */}
        <Box className="logo">
          <Link to="/">
            <img
              src={isDark
                ? "/logo/NextternSMSLogo-ForDarkBg.png"
                : "/logo/NextternSMSLogo-ForLightBg.png"
              }
              alt="logo-icon"
              width={40}
              height={26}
              loading="eager"
            />
            <span>
              <img
                src={isDark
                  ? "/logo/NextternSMSName-ForDarkBg.png"
                  : "/logo/NextternSMSName-ForLightBg.png"
                }
                alt="logo-name"
                style={{ width: "210px", height: "auto", marginTop: "8px"}}
                loading="eager"
              />
            </span>
          </Link>
        </Box>

        {/* ── Burger (hidden on mobile) ── */}
        <Box className="burger-menu" onClick={toggleActive} sx={{ display: { xs: "none", md: "flex" } }}>
          <Typography component={"span"} className="top-bar"></Typography>
          <Typography component={"span"} className="middle-bar"></Typography>
          <Typography component={"span"} className="bottom-bar"></Typography>
        </Box>

        <Box className="sidebar-inner">
          <Box className="sidebar-menu">

            {/* Dashboard — always visible */}
            <Link to="/" className={`sidebar-menu-link ${isActiveLink("/")}`}>
              <i className="material-symbols-outlined">dashboard</i>
              <Typography component={"span"} className="title">Dashboard</Typography>
            </Link>

            {/* ── Menu items — skeleton while loading, real items after ── */}
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <MenuItemSkeleton key={i} />
              ))
            ) : (
              menus.map((menu) => {

                /* CASE A: Direct-link menu (has route, no sub-pages) */
                if (menu.route && (!menu.pages || menu.pages.length === 0)) {
                  return (
                    <Link
                      key={menu._id}
                      to={menu.route}
                      className={`sidebar-menu-link ${isActiveLink(menu.route)}`}
                    >
                      <i className="material-symbols-outlined">{menu.icon}</i>
                      <Typography component={"span"} className="title">{menu.name}</Typography>
                    </Link>
                  );
                }

                /* CASE B: Accordion menu (has sub-pages) */
                if (!menu.pages?.length) return null;
                const visiblePages = menu.pages.filter((p) => !p.hide);
                if (!visiblePages.length) return null;

                return (
                  <Accordion
                    key={menu._id}
                    expanded={expanded === menu._id}
                    onChange={handleChange(menu._id)}
                    className="mat-accordion"
                  >
                    <AccordionSummary
                      className="mat-summary"
                      aria-controls={`${menu._id}-content`}
                      id={`${menu._id}-header`}
                    >
                      <i className="material-symbols-outlined">{menu.icon}</i>
                      <Typography component={"span"} className="title">{menu.name}</Typography>
                    </AccordionSummary>

                    <AccordionDetails className="mat-details">
                      <ul className="sidebar-sub-menu">
                        {menu.pages.filter((page) => !page.hide).map((page) => (
                          <li key={page.key} className="sidemenu-item">
                            <Link
                              to={page.route}
                              className={`sidemenu-link ${isActiveLink(page.route)}`}
                            >
                              {page.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </AccordionDetails>
                  </Accordion>
                );
              })
            )}

            {/* Logout — always visible */}
            <Box
              className="sidebar-menu-link"
              onClick={() => { dispatch(logout()); navigate("/sign-in"); }}
              sx={{ cursor: "pointer" }}
            >
              <i className="material-symbols-outlined">logout</i>
              <Typography component={"span"} className="title">Logout</Typography>
            </Box>

          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LeftSidebarMenu;